import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ServiceQuoteModal } from "@/components/ServiceQuoteModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Warehouse, Package, Thermometer, Shield, Clock, CheckCircle, Truck, BarChart3 } from "lucide-react";
import warehouseImage from "@/assets/warehouse.jpg";

const Warehousing = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        {/* Hero Section */}
        <section className="relative h-[60vh] mb-16 rounded-lg overflow-hidden mx-4">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${warehouseImage})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/60" />
          </div>
          <div className="relative z-10 container mx-auto px-4 h-full flex items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-primary-foreground mb-6">
                Warehousing & Distribution
              </h1>
              <p className="text-xl text-primary-foreground/90 max-w-2xl mb-8">
                State-of-the-art warehouse facilities in California. Temperature-controlled storage, inventory management, and value-added logistics services.
              </p>
              <Button size="lg" onClick={() => setQuoteModalOpen(true)}>
                Request Warehouse Quote
              </Button>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4">
          {/* Overview */}
          <section className="mb-16">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-4xl font-bold mb-6">Premium Warehouse Facilities in California</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Our strategically located warehouses provide comprehensive storage and distribution solutions. With over 50,000 square meters of space, advanced WMS technology, and 24/7 security, we ensure your inventory is safe and accessible.
              </p>
              <p className="text-lg text-muted-foreground">
                From short-term storage to complex fulfillment operations, our California facilities are equipped to handle any requirement. ISO-certified operations with full traceability and real-time inventory visibility.
              </p>
            </div>
          </section>

          {/* Facilities Overview */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Facility Specifications</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { label: "Total Space", value: "50,000 m²", icon: Warehouse },
                { label: "Ceiling Height", value: "12 meters", icon: BarChart3 },
                { label: "Pallet Positions", value: "25,000+", icon: Package },
                { label: "Temp Zones", value: "3 zones", icon: Thermometer },
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <Card key={idx} className="border-2">
                    <CardContent className="p-6 text-center">
                      <Icon className="h-10 w-10 text-primary mx-auto mb-3" />
                      <div className="text-3xl font-bold text-primary mb-1">{item.value}</div>
                      <div className="text-sm text-muted-foreground">{item.label}</div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Services */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Warehouse Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="border-2 hover:border-primary transition-colors">
                <CardContent className="p-8">
                  <Package className="h-12 w-12 text-primary mb-4" />
                  <h3 className="text-2xl font-bold mb-4">Storage Solutions</h3>
                  <p className="text-muted-foreground mb-6">
                    Flexible storage options from short-term to long-term contracts. Pallet racking, bulk storage, and specialized zones for hazardous materials.
                  </p>
                  <ul className="space-y-2 mb-6">
                    {["Short-term (1-6 months)", "Long-term inventory management", "Bonded warehouse options", "Climate-controlled zones", "Security 24/7 CCTV"].map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant="outline" className="w-full" onClick={() => setQuoteModalOpen(true)}>Get Storage Quote</Button>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary transition-colors">
                <CardContent className="p-8">
                  <Truck className="h-12 w-12 text-primary mb-4" />
                  <h3 className="text-2xl font-bold mb-4">Distribution & Fulfillment</h3>
                  <p className="text-muted-foreground mb-6">
                    End-to-end order fulfillment services. Pick, pack, and ship operations with same-day dispatch capability for orders received before 2 PM.
                  </p>
                  <ul className="space-y-2 mb-6">
                    {["Order picking & packing", "Cross-docking services", "E-commerce fulfillment", "Returns handling", "Same-day dispatch available"].map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant="outline" className="w-full" onClick={() => setQuoteModalOpen(true)}>Discuss Fulfillment</Button>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary transition-colors">
                <CardContent className="p-8">
                  <Thermometer className="h-12 w-12 text-primary mb-4" />
                  <h3 className="text-2xl font-bold mb-4">Temperature-Controlled Storage</h3>
                  <p className="text-muted-foreground mb-6">
                    Three temperature zones: ambient, chilled (2-8°C), and frozen (-18°C to -25°C). Perfect for pharmaceuticals, food, and sensitive materials.
                  </p>
                  <ul className="space-y-2 mb-6">
                    {["Pharmaceutical-grade storage", "Food-safe certified", "Continuous temperature monitoring", "Backup power systems", "Temperature mapping reports"].map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant="outline" className="w-full" onClick={() => setQuoteModalOpen(true)}>Cold Chain Quote</Button>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary transition-colors">
                <CardContent className="p-8">
                  <BarChart3 className="h-12 w-12 text-primary mb-4" />
                  <h3 className="text-2xl font-bold mb-4">Value-Added Services</h3>
                  <p className="text-muted-foreground mb-6">
                    Comprehensive value-added logistics including kitting, labeling, quality inspection, repackaging, and custom assembly operations.
                  </p>
                  <ul className="space-y-2 mb-6">
                    {["Product labeling & kitting", "Quality control inspection", "Repackaging & bundling", "Product assembly", "Customization services"].map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant="outline" className="w-full" onClick={() => setQuoteModalOpen(true)}>Explore VAS</Button>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Technology */}
          <section className="mb-16 bg-secondary rounded-lg p-12">
            <h2 className="text-3xl font-bold text-center mb-12">Warehouse Management System</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <Clock className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Real-time Tracking</h3>
                <p className="text-muted-foreground">
                  Live inventory visibility through our WMS portal. Track stock levels, movements, and orders in real-time from anywhere.
                </p>
              </div>
              <div className="text-center">
                <BarChart3 className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Advanced Analytics</h3>
                <p className="text-muted-foreground">
                  Detailed reporting on inventory turns, order accuracy, and warehouse performance. Data-driven insights for optimization.
                </p>
              </div>
              <div className="text-center">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">API Integration</h3>
                <p className="text-muted-foreground">
                  Seamless integration with your ERP, e-commerce platforms, and order management systems via RESTful API.
                </p>
              </div>
            </div>
            <div className="text-center">
              <Button size="lg" onClick={() => setQuoteModalOpen(true)}>Schedule Facility Tour</Button>
            </div>
          </section>

          {/* Security Features */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Security & Compliance</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: "24/7 CCTV", desc: "Complete video surveillance with 90-day retention" },
                { title: "Access Control", desc: "Biometric and card-based entry systems" },
                { title: "Fire Suppression", desc: "Advanced fire detection and suppression systems" },
                { title: "ISO Certified", desc: "ISO 9001 quality management certification" },
                { title: "Insurance", desc: "Comprehensive cargo insurance available" },
                { title: "Pest Control", desc: "Regular inspections and preventive measures" },
                { title: "Security Guards", desc: "On-site security personnel 24/7" },
                { title: "Audit Trail", desc: "Complete movement and handling records" },
              ].map((item, idx) => (
                <Card key={idx}>
                  <CardContent className="p-6">
                    <Shield className="h-8 w-8 text-primary mb-3" />
                    <h3 className="font-bold mb-2">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Pricing */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Flexible Pricing Plans</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { 
                  title: "Short-Term Storage", 
                  price: "€15/pallet/month", 
                  features: ["1-6 months contract", "Standard handling included", "Basic WMS access", "Weekly reporting"] 
                },
                { 
                  title: "Long-Term Storage", 
                  price: "€10/pallet/month", 
                  features: ["6+ months contract", "Dedicated account manager", "Full WMS integration", "Daily reporting", "Priority handling"] 
                },
                { 
                  title: "Full Fulfillment", 
                  price: "Custom pricing", 
                  features: ["Complete 3PL service", "Pick, pack & ship", "Returns handling", "VAS included", "API integration"] 
                },
              ].map((plan, idx) => (
                <Card key={idx} className={idx === 1 ? "border-2 border-primary" : "border-2"}>
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold mb-2">{plan.title}</h3>
                    <div className="text-3xl font-bold text-primary mb-6">{plan.price}</div>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      variant={idx === 1 ? "default" : "outline"} 
                      className="w-full"
                      onClick={() => setQuoteModalOpen(true)}
                    >
                      Get Quote
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-primary text-primary-foreground rounded-lg p-12 text-center">
            <h2 className="text-3xl font-bold mb-6">Visit Our California Facilities</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Schedule a tour of our state-of-the-art warehouse facilities. See our operations firsthand and discuss your specific requirements with our team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" onClick={() => setQuoteModalOpen(true)}>
                Schedule Facility Tour
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
                Contact Warehouse Team
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <ServiceQuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} serviceType="warehousing" />
    </div>
  );
};

export default Warehousing;
